import { L } from '@lib';
import {
  DispachedServicePart,
  InvoicePayment,
  ServiceOrder,
  ServicePart,
  TransactionType,
} from '@services';
import { RcFile } from 'antd/lib/upload';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

import * as abpTypings from '../lib/abp';

declare const abp: any;

/** A file ready to post: base64 without the data: prefix, plus what it ended up being. */
export interface CompressedImage {
  content: string;
  contentType: string;
  name: string;
}

interface IPriceInput {
  minPrice: number;
  maxPrice?: number;
  hideCurrency?: boolean;
  currency?: string;
}

type currency = '$' | '£';
type timeUnit = 'hour' | 'hours';

interface IRangeInput {
  minValue: number;
  maxValue?: number;
  valueSeparator?: string;
  hideUnit?: boolean;
  unit?: timeUnit | currency;
  unitOnBothSides?: boolean;
  spaceBetweenRangeValues?: boolean;
  unitHasSpacing?: boolean;
}

interface IStartEndDate {
  startAt?: Date | string | null;
  endAt?: Date | string | null;
}

interface ILabourPrice {
  price?: number;
  discount?: number;
  duration?: number;
  isFixed: boolean;
}

interface IServiceTotalPrice {
  price: number;
  discount?: number;
  duration: number;
  vat: number;
  isVat: boolean;
  isFixed: boolean;
  serviceParts: DispachedServicePart[];
}

interface IRowPrice {
  vat?: number;
  price?: number;
  discount?: number;
  duration?: number;
  isFixed?: boolean;
}

class Utils {
  loadScript(url: string) {
    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = url;
    document.body.appendChild(script);
  }

  extend(...args: any[]) {
    let options,
      name,
      src,
      srcType,
      copy,
      copyIsArray,
      clone,
      target = args[0] || {},
      i = 1,
      length = args.length,
      deep = false;
    if (typeof target === 'boolean') {
      deep = target;
      target = args[i] || {};
      i++;
    }
    if (typeof target !== 'object' && typeof target !== 'function') {
      target = {};
    }
    if (i === length) {
      target = this;
      i--;
    }
    for (; i < length; i++) {
      if ((options = args[i]) !== null) {
        for (name in options) {
          src = target[name];
          copy = options[name];
          if (target === copy) {
            continue;
          }
          srcType = Array.isArray(src) ? 'array' : typeof src;
          if (deep && copy && ((copyIsArray = Array.isArray(copy)) || typeof copy === 'object')) {
            if (copyIsArray) {
              copyIsArray = false;
              clone = src && srcType === 'array' ? src : [];
            } else {
              clone = src && srcType === 'object' ? src : {};
            }
            target[name] = this.extend(deep, clone, copy);
          } else if (copy !== undefined) {
            target[name] = copy;
          }
        }
      }
    }

    return target;
  }

  setLocalization() {
    if (!abp.utils.getCookieValue('ZeroG33K.Localization.CultureName')) {
      let language = navigator.language;
      abp.utils.setCookieValue(
        'ZeroG33K.Localization.CultureName',
        language,
        new Date(new Date().getTime() + 5 * 365 * 86400000),
        abp.appPath
      );
    }
  }

  getMobileAppUser = (members: { [key: string]: any }[] | null): { [key: string]: any } | null => {
    if (!members || members.length === 0) {
      return null;
    }

    const mobileUser = members.find((member: any) => {
      return !isNaN(Number(member.user.id));
    });

    return mobileUser || null;
  };

  getGarage = (members: { [key: string]: any }[] | null): { [key: string]: any } | null => {
    if (!members || members.length === 0) {
      return null;
    }

    const garageUser = members.find((member: any) => {
      const userId = member.user.id;
      return /^[a-zA-Z0-9]+$/.test(userId) && /[a-zA-Z]/.test(userId); // Ensure it's alphanumeric but has at least one letter
    });

    return garageUser || null;
  };

  getChatMembers = (channel: any): { [key: string]: any }[] | null => {
    if (!channel || !channel.state || !channel.state.members) {
      return null;
    }

    return Object.values(channel.state.members); // Convert members object to an array of member objects
  };

  formatChatText = (text: string | null | undefined): string | null => {
    if (!text) return null;
    return text.length > 70 ? text.slice(0, 70) + '...' : text;
  };

  getCurrentClockProvider(currentProviderName: string): abpTypings.timing.IClockProvider {
    if (currentProviderName === 'unspecifiedClockProvider') {
      return abp.timing.unspecifiedClockProvider;
    }

    if (currentProviderName === 'utcClockProvider') {
      return abp.timing.utcClockProvider;
    }

    return abp.timing.localClockProvider;
  }

  formatCurrency(price: number) {
    const normalized = price || 0;
    return normalized.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      style: 'currency',
      currency: 'GBP',
    });
  }

  formatPrice({ minPrice, maxPrice, hideCurrency, currency }: IPriceInput) {
    return `${hideCurrency ? '' : currency || '£'}${minPrice || ''} ${
      maxPrice ? ` - ${hideCurrency ? '' : currency || '£'}${maxPrice || ''}` : ''
    }`;
  }

  formatNumber = (value: number, decimalNumber: number): string => {
    return value.toFixed(decimalNumber);
  };

  formatNumberOrPercentage(value: number, isPercentage: boolean = false): string {
    if (value === undefined || value === null) {
      return '';
    }
    if (isPercentage) {
      return `${this.roundToTwoDecimals(value * 100)}%`;
    }
    const hasDecimals = value % 1 !== 0;
    return hasDecimals ? this.roundToTwoDecimals(value).toString() : value.toString();
  }

  formatVat = (vat: number | undefined, isVatApplicable: boolean): string => {
    return isVatApplicable ? `${vat || 0}%` : '0%';
  };

  /** Label shown for a service in the pickers: '<code> <category> - <name>'.
   *  The code is optional, so services without one keep the plain '<category> - <name>'. */
  formatServiceLabel = (
    service: { code?: string; name?: string; normalizedName?: string },
    cleanName?: boolean
  ): string => {
    const label = (cleanName ? service.name : service.normalizedName) ?? service.name ?? '';
    return service.code ? `${service.code} ${label}` : label;
  };

  formatAddress(
    address?: string | null,
    secondAddress?: string | null,
    thirdAddress?: string | null
  ): string | null {
    const addressParts: string[] = [];

    if (address) {
      addressParts.push(address);
    }
    if (secondAddress) {
      addressParts.push(secondAddress);
    }
    if (thirdAddress) {
      addressParts.push(thirdAddress);
    }

    if (addressParts.length > 0) {
      return addressParts.join(', ');
    } else {
      return null;
    }
  }

  formatPhone(
    mobilePhone?: string | null,
    landLine?: string | null
  ): Array<{ type: 'mobile' | 'landline'; number: string }> | null {
    const phoneNumbers: Array<{ type: 'mobile' | 'landline'; number: string }> = [];

    if (mobilePhone) {
      phoneNumbers.push({ type: 'mobile', number: mobilePhone });
    }

    if (landLine) {
      phoneNumbers.push({ type: 'landline', number: landLine });
    }

    return phoneNumbers.length > 0 ? phoneNumbers : null;
  }

  formatRange({
    minValue,
    maxValue,
    valueSeparator,
    hideUnit,
    unit,
    unitOnBothSides,
    unitHasSpacing,
  }: IRangeInput) {
    return `${minValue}${unitHasSpacing ? ' ' : ''}${
      maxValue ? (unitOnBothSides ? L(unit || '') || L('unit') : '') : ''
    }${maxValue ? ` ${valueSeparator ? valueSeparator : '-'} ${maxValue}` : ''}${
      unitHasSpacing ? ' ' : ''
    }${hideUnit ? '' : unit || L('unit')}
    `;
  }
  formatTime(timeOffset: string) {
    return dayjs(
      timeOffset !== '00:00' ? dayjs(timeOffset).utc().format('HH:mm') : timeOffset,
      'HH:mm'
    );
  }
  displayTime = (time: string): dayjs.Dayjs => {
    if (time.includes('T')) {
      return dayjs(time);
    } else {
      return dayjs(time, 'HH:mm');
    }
  };

  formatDateTime(date: Date | string) {
    return (date && dayjs(date).format('DD/MM/YY HH:mm')) || '';
  }

  formatDate(date: Date | string) {
    return (date && dayjs(date).format('DD/MM/YY')) || '';
  }

  formatDropOffDateFromDraggable(inputDate: string) {
    const dateTimePart = inputDate.split('Drop: ')[1];

    // Parse date and time parts
    const [datePart, timePart] = dateTimePart.split(' ');
    const [day, month, year] = datePart.split('/');

    const formattedDateTime = `20${year}-${month}-${day} ${timePart}`;
    return dayjs(formattedDateTime);
  }

  getHourAndMinutesDifference({ startAt, endAt }: IStartEndDate) {
    const startTime = dayjs(startAt);
    const endTime = dayjs(endAt);

    const hours = endTime.diff(startTime, 'hour');
    const minutes = endTime.diff(startTime, 'minute') % 60;

    return `${hours === 0 ? '' : `${hours}h`}${
      minutes === 60 || minutes === 0 ? '' : `:${minutes}m`
    }`;
  }

  getHourDifference({ startAt, endAt }: IStartEndDate) {
    const start = dayjs(startAt);
    const end = dayjs(endAt);

    // Calculate the difference in minutes
    const diffInMinutes = end.diff(start, 'minutes');

    // Convert the difference to a decimal number
    const decimalHours = diffInMinutes / 60;

    return decimalHours;
  }

  calculateTotalAmount(serviceOrders: ServiceOrder[]) {
    return serviceOrders.reduce((prev, curr) => {
      const labourPrice = curr.isFixed ? curr.price || 0 : (curr.price || 0) * curr.duration;
      const labourDiscount = (labourPrice * (curr.discount || 0)) / 100;
      const labourTotal = prev + labourPrice - labourDiscount;

      const extrasTotal = curr.extras.reduce((previous, current) => {
        const extraPrice = (current.price || 0) * current.quantity;
        const extraDiscount = (extraPrice * (current.discount || 0)) / 100;
        return previous + extraPrice - extraDiscount;
      }, 0);

      const jobTotal = labourTotal + extrasTotal;

      return this.roundToTwoDecimals(jobTotal);
    }, 0);
  }

  calculateTotalVat(serviceOrders: ServiceOrder[]) {
    return serviceOrders.reduce((prev, curr) => {
      const labourPrice = curr.isFixed ? curr.price || 0 : (curr.price || 0) * curr.duration;
      const labourDiscount = (labourPrice * curr.discount) / 100;
      const labourPriceWithDiscount = labourPrice - labourDiscount;
      const labourVat = this.roundToTwoDecimals((labourPriceWithDiscount * curr.vat) / 100);
      const labourVatTotal = prev + labourVat;

      const extrasVatTotal = curr.extras.reduce((previous, current) => {
        const extraPrice = (current.price || 0) * current.quantity;
        const extraDiscount = (extraPrice * (current.discount || 0)) / 100;
        const extraWithDiscount = extraPrice - extraDiscount;
        const extraVat = this.roundToTwoDecimals((extraWithDiscount * (current.vat || 0)) / 100);
        return previous + extraVat;
      }, 0);

      const vatTotal = labourVatTotal + extrasVatTotal;

      return this.roundToTwoDecimals(vatTotal);
    }, 0);
  }

  calculatePaymentTotal(payments: InvoicePayment[]) {
    return this.roundToTwoDecimals(
      this.calculateInvoiceTotals(
        [TransactionType.Payment, TransactionType.BankTransfer, TransactionType.OtherPayment],
        payments
      )
    );
    //return this.roundToTwoDecimals(this.calculateInvoiceTotals(TransactionType.Payment, payments));
  }

  calculateCreditTotal(payments: InvoicePayment[]) {
    return this.calculateInvoiceTotals(TransactionType.Credit, payments);
  }

  calculateRefundPaidTotal(payments: InvoicePayment[]) {
    return this.calculateInvoiceTotals(TransactionType.RefundPaid, payments);
  }

  calculateRefundRequestedTotal(payments: InvoicePayment[]) {
    return this.calculateInvoiceTotals(TransactionType.RefundRequest, payments);
  }

  // calculateInvoiceTotals(transactionType: TransactionType, payments: InvoicePayment[]) {
  //   return payments
  //     .filter((payment) => payment.transactionType === transactionType)
  //     .reduce((prev, curr) => prev + curr.amount, 0);
  // }
  calculateInvoiceTotals(
    transactionTypes: TransactionType | TransactionType[],
    payments: InvoicePayment[]
  ) {
    const typesArray = Array.isArray(transactionTypes) ? transactionTypes : [transactionTypes];
    return payments
      .filter((payment) => typesArray.includes(payment.transactionType))
      .reduce((prev, curr) => prev + curr.amount, 0);
  }

  calculateServiceWithVatAndPartsTotal(service: IServiceTotalPrice) {
    const {
      price = 0,
      isVat = false,
      vat = 0,
      discount = 0,
      duration = 1,
      isFixed = false,
      serviceParts = [],
    } = service;
    const servicePrice = isFixed ? price : price * duration;
    const serviceDiscount = (servicePrice * discount) / 100;
    const priceWithDiscount = servicePrice - serviceDiscount;
    const priceVat = isVat ? this.roundToTwoDecimals((priceWithDiscount * vat) / 100) : 0;
    const partsPrice = serviceParts.reduce((prev, curr) => {
      const partPrice = (curr?.price || 0) * (curr?.quantity || 0);
      const partDiscount = (partPrice * (curr?.discount || 0)) / 100;
      const partPriceWithDiscount = partPrice - partDiscount;
      const partVat = this.roundToTwoDecimals((partPriceWithDiscount * vat) / 100);
      return prev + partPriceWithDiscount + partVat;
    }, 0);
    return this.roundToTwoDecimals(priceWithDiscount + priceVat + partsPrice);
  }

  calculateLabourPrice({ isFixed = false, price = 0, discount = 0, duration = 1 }: ILabourPrice) {
    const servicePrice = isFixed ? price : price * duration;
    const serviceDiscount = (discount && (servicePrice * discount) / 100) || 0;
    const priceWithDiscount = servicePrice - serviceDiscount;
    return this.roundToTwoDecimals(priceWithDiscount);
  }

  calculatePartsPrice(parts: ServicePart[] | DispachedServicePart[]) {
    const partPrice = parts.reduce((prev, curr) => {
      const partPrice = (curr?.price || 0) * (curr?.quantity || 0);
      const priceWithDiscount = partPrice - (partPrice * (curr?.discount || 0)) / 100;
      return prev + priceWithDiscount;
    }, 0);

    return this.roundToTwoDecimals(partPrice);
  }

  calculateVat({ isFixed = false, price = 0, discount = 0, duration = 1, vat = 0 }) {
    const vatPrice = isFixed ? price : price * duration;
    const priceWithDiscount = this.roundToTwoDecimals(vatPrice - (vatPrice * discount) / 100);
    return this.roundToTwoDecimals((priceWithDiscount * vat) / 100);
  }

  calculateRowTotalPrice({
    vat = 0,
    price = 0,
    discount = 0,
    duration = 1,
    isFixed = false,
  }: IRowPrice) {
    const totalPrice = isFixed ? price : price * duration;
    const totalDiscount = (totalPrice * discount) / 100;
    const priceWithDiscount = this.roundToTwoDecimals(totalPrice - totalDiscount);
    const totalVat = this.roundToTwoDecimals((priceWithDiscount * vat) / 100);
    return this.roundToTwoDecimals(priceWithDiscount + totalVat);
  }

  getBase64 = (file: RcFile, callback: (url: string) => void) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result as string));
    reader.readAsDataURL(file);
  };

  /**
   * Shrinks a photo before it goes near the network. A phone camera file is 4-6MB and roughly a third
   * bigger again once base64 encoded; at 1600px it lands around 200-400KB, which is more than a
   * checklist ever renders. Anything that is not an image, or that the browser cannot decode, is
   * passed through untouched - a photo that uploads big beats one that does not upload.
   */
  compressImage = (
    file: File,
    maxEdge: number = 1600,
    quality: number = 0.7
  ): Promise<CompressedImage> =>
    new Promise<CompressedImage>((resolve, reject) => {
      const passThrough = () => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve({
            content: (reader.result as string).split(',')[1],
            contentType: file.type || 'application/octet-stream',
            name: file.name,
          });
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      };

      if (!file.type?.startsWith('image/')) {
        passThrough();
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);

        const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);

        const context = canvas.getContext('2d');
        if (!context || !canvas.width || !canvas.height) {
          passThrough();
          return;
        }

        // JPEG has no alpha channel, so anything transparent - a PNG screenshot of a diagnostic
        // readout, say - would come out on a black background without this.
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        resolve({
          content: canvas.toDataURL('image/jpeg', quality).split(',')[1],
          contentType: 'image/jpeg',
          name: file.name.replace(/\.[^.]+$/, '') + '.jpg',
        });
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        passThrough();
      };

      image.src = objectUrl;
    });

  getBase64FromUrl = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        debugger
        const base64Data = (reader.result as string).split(',')[1]; // strip "data:image/png;base64,"
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  getFileType = (file: RcFile) =>
    file.type.startsWith('image') ? 'Image' : file.type.startsWith('video') ? 'Video' : 'Document';

  documentDownload = (data: any) => {
    const linkSource = `data:${data.contentType};base64,${data.base64Content}`;
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = data.name;
    downloadLink.click();
  };

  configureUrl = (website: URL | undefined): URL | undefined => {
    if (!website) return undefined;

    const isHttp = website?.toString().substring(0, 'http://'.length) === 'http://';
    const isHttps = website?.toString().substring(0, 'https://'.length) === 'https://';

    if (!isHttp && !isHttps) {
      website = new URL(`https://${website}`);
    }

    return website;
  };

  roundToTwoDecimals = (amount: number): number => {
    return Math.round((amount + Number.EPSILON) * 100) / 100;
  };

  decimalHoursToHoursMinutes = (value: number) => {
    const duration = value === 0 ? 1 : value;
    const hours = Math.floor(duration);
    const minutes = Math.round((duration - hours) * 60);

    // Create a Day.js object with the specific time
    const time = dayjs().startOf('day').add(hours, 'hour').add(minutes, 'minute');
    // Format the time
    const formattedTime = time.format('HH:mm');

    return formattedTime;
  };

  isDateValid = (date?: Date | string) =>
    date ? new Date(date).getDate() !== new Date('0001-01-01').getDate() : false;

  isNullOrUndefined = (value: any) => value !== null && value !== undefined;

  // Function to filter the Map based on a condition
  filterMap = (map: any, filterCondition: any) => {
    let filteredMap = new Map();
    for (let [key, value] of map) {
      if (filterCondition(value, key)) {
        filteredMap.set(key, value);
      }
    }
    return filteredMap;
  };

  getStatusTagColor = (status: string): string => {
    switch (status) {
      case 'Paid':
        return 'green';
      case 'Unpaid':
        return 'red';
      case 'Partially Paid':
        return 'orange';
      default:
        return 'default';
    }
  };
}

export default new Utils();
